import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  IdentificationCard, 
  CheckCircle,
  Clock,
  Warning,
  Upload,
  File,
  Trash,
  Eye,
  Download,
  X,
  ArrowRight,
  Bank,
  Buildings,
  FileText
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_TYPES = [
  'Sole Proprietorship',
  'Partnership',
  'Private Limited Company',
  'Public Limited Company',
  'Limited Liability Partnership (LLP)',
  'One Person Company (OPC)',
  'Hindu Undivided Family (HUF)',
  'Trust',
  'Society',
  'Other'
];

const DOCUMENT_TYPES = [
  { value: 'gst_certificate', label: 'GST Certificate', required: true, description: 'GST registration certificate' },
  { value: 'pan_card', label: 'PAN Card', required: true, description: 'Business or individual PAN card' },
  { value: 'cancelled_cheque', label: 'Cancelled Cheque', required: true, description: 'Cancelled cheque of bank account' },
  { value: 'address_proof', label: 'Address Proof', required: true, description: 'Utility bill, rent agreement, etc.' },
  { value: 'business_registration', label: 'Business Registration', required: false, description: 'Certificate of Incorporation, Partnership Deed, etc.' },
  { value: 'bank_statement', label: 'Bank Statement', required: false, description: 'Last 3 months bank statement' },
  { value: 'other', label: 'Other Document', required: false, description: 'Any other supporting document' }
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
  'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep'
];

export default function KycPage() {
  const { user, refreshAuth } = useAuth();
  const [kycData, setKycData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  
  const [formData, setFormData] = useState({
    // Business Info
    business_name: '',
    business_type: '',
    business_address: '',
    business_city: '',
    business_state: '',
    business_pincode: '',
    // Tax Info
    gst_number: '',
    pan_number: '',
    // Bank Info
    bank_name: '',
    bank_account_number: '',
    bank_account_holder: '',
    bank_ifsc: ''
  });

  const fetchKycData = useCallback(async () => {
    try {
      const [statusRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/kyc/status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/kyc/documents`, { withCredentials: true })
      ]);
      
      setKycData(statusRes.data.kyc_data);
      setDocuments(docsRes.data.documents || []);
      
      // Populate form if data exists
      if (statusRes.data.kyc_data) {
        const kyc = statusRes.data.kyc_data;
        setFormData({
          business_name: kyc.business_name || '',
          business_type: kyc.business_type || '',
          business_address: kyc.business_address || '',
          business_city: kyc.business_city || '',
          business_state: kyc.business_state || '',
          business_pincode: kyc.business_pincode || '',
          gst_number: kyc.gst_number || '',
          pan_number: kyc.pan_number || '',
          bank_name: kyc.bank_name || '',
          bank_account_number: kyc.bank_account_number || '',
          bank_account_holder: kyc.bank_account_holder || '',
          bank_ifsc: kyc.bank_ifsc || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch KYC data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKycData();
  }, [fetchKycData]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!selectedDocType) {
      toast.error('Please select a document type');
      return;
    }
    
    // Validate file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, JPG, or PNG');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/kyc/upload-document?document_type=${selectedDocType}`,
        formDataUpload,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      toast.success('Document uploaded successfully');
      setDocuments([...documents, response.data]);
      setUploadDialogOpen(false);
      setSelectedDocType('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/kyc/document/${documentId}`, { withCredentials: true });
      setDocuments(documents.filter(d => d.document_id !== documentId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields = [
      { key: 'business_name', label: 'Business Name' },
      { key: 'business_type', label: 'Business Type' },
      { key: 'gst_number', label: 'GST Number' },
      { key: 'pan_number', label: 'PAN Number' },
      { key: 'bank_name', label: 'Bank Name' },
      { key: 'bank_account_number', label: 'Bank Account Number' },
      { key: 'bank_ifsc', label: 'IFSC Code' }
    ];
    
    const missing = requiredFields.filter(f => !formData[f.key]);
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    
    // Validate GST format
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstRegex.test(formData.gst_number.toUpperCase())) {
      toast.error('Invalid GST number format');
      return;
    }
    
    // Validate PAN format
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(formData.pan_number.toUpperCase())) {
      toast.error('Invalid PAN number format');
      return;
    }
    
    // Check required documents
    const requiredDocs = DOCUMENT_TYPES.filter(d => d.required);
    const uploadedTypes = documents.map(d => d.document_type);
    const missingDocs = requiredDocs.filter(d => !uploadedTypes.includes(d.value));
    
    if (missingDocs.length > 0) {
      toast.error(`Please upload: ${missingDocs.map(d => d.label).join(', ')}`);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/kyc/submit`, formData, { withCredentials: true });
      toast.success('KYC submitted for review');
      refreshAuth();
      fetchKycData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit KYC');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = () => {
    switch (user?.kyc_status) {
      case 'approved':
        return <CheckCircle size={56} className="text-[#28cd8c]" weight="fill" />;
      case 'submitted':
      case 'under_review':
        return <Clock size={56} className="text-[#f6a609]" weight="fill" />;
      case 'rejected':
        return <X size={56} className="text-[#ea4b5a]" weight="fill" />;
      case 'resubmission_required':
        return <Warning size={56} className="text-[#FF9500]" weight="fill" />;
      default:
        return <IdentificationCard size={56} className="text-[#7d64ff]" weight="fill" />;
    }
  };

  const getStatusConfig = () => {
    switch (user?.kyc_status) {
      case 'approved':
        return { 
          title: 'KYC Verified', 
          description: 'Your business verification is complete. You can now access all features.',
          color: 'border-[#28cd8c] bg-[#28cd8c]/5'
        };
      case 'submitted':
        return { 
          title: 'Under Review', 
          description: 'Your KYC documents are being reviewed. This usually takes 1-2 business days.',
          color: 'border-[#f6a609] bg-[#f6a609]/5'
        };
      case 'under_review':
        return { 
          title: 'Under Review', 
          description: 'An admin is currently reviewing your documents.',
          color: 'border-[#f6a609] bg-[#f6a609]/5'
        };
      case 'rejected':
        return { 
          title: 'Verification Failed', 
          description: kycData?.rejection_reason || 'Your KYC was rejected. Please review and resubmit.',
          color: 'border-[#ea4b5a] bg-[#ea4b5a]/5'
        };
      case 'resubmission_required':
        return { 
          title: 'Resubmission Required', 
          description: kycData?.resubmission_reason || 'Please update your documents and resubmit.',
          color: 'border-[#FF9500] bg-[#FF9500]/5'
        };
      default:
        return { 
          title: 'Complete Your KYC', 
          description: 'Submit your business documents to start selling on the marketplace.',
          color: 'border-[#e5e5ea]'
        };
    }
  };

  const getDocumentLabel = (type) => {
    return DOCUMENT_TYPES.find(d => d.value === type)?.label || type;
  };

  if (loading) {
    return (
      <DashboardLayout title="KYC Verification">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statusConfig = getStatusConfig();
  const canEdit = !['approved', 'submitted', 'under_review'].includes(user?.kyc_status);

  return (
    <DashboardLayout title="KYC Verification" subtitle="Business verification and compliance documents">
      {/* Status Card */}
      <div className={`bg-white border-2 p-8 mb-6 ${statusConfig.color}`}>
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            {getStatusIcon()}
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-2xl text-[#0A0A0A] mb-2">{statusConfig.title}</h3>
            <p className="text-[#5a5c87]">{statusConfig.description}</p>
            
            {kycData?.review_notes?.length > 0 && (
              <div className="mt-4 p-4 bg-white border border-[#e5e5ea] rounded">
                <h4 className="font-semibold text-sm mb-2">Review History</h4>
                {kycData.review_notes.slice(-3).reverse().map((note, idx) => (
                  <div key={idx} className="text-sm text-[#5a5c87] mb-2 last:mb-0">
                    <span className="font-medium">{note.action}:</span> {note.note}
                    <span className="text-xs ml-2">({new Date(note.timestamp).toLocaleDateString()})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KYC Form - Only show if editable */}
      {canEdit ? (
        <div className="space-y-6">
          {/* Progress Steps */}
          <div className="bg-white border border-[#e5e5ea] p-4">
            <div className="flex items-center justify-between">
              {[
                { step: 1, label: 'Business Info', icon: Buildings },
                { step: 2, label: 'Tax & Bank', icon: Bank },
                { step: 3, label: 'Documents', icon: FileText },
                { step: 4, label: 'Review', icon: CheckCircle }
              ].map((item, idx) => (
                <div key={item.step} className="flex items-center">
                  <button
                    onClick={() => setCurrentStep(item.step)}
                    className={`flex items-center gap-2 px-4 py-2 rounded transition-all ${
                      currentStep === item.step 
                        ? 'bg-[#7d64ff] text-white' 
                        : currentStep > item.step
                        ? 'bg-[#28cd8c]/10 text-[#28cd8c]'
                        : 'bg-[#fcfcfc] text-[#5a5c87]'
                    }`}
                  >
                    <item.icon size={20} />
                    <span className="hidden sm:inline font-medium">{item.label}</span>
                  </button>
                  {idx < 3 && (
                    <ArrowRight size={20} className="mx-2 text-[#e5e5ea]" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step 1: Business Information */}
          {currentStep === 1 && (
            <div className="bg-white border border-[#e5e5ea] p-6">
              <h3 className="font-bold text-lg text-[#0A0A0A] mb-6 flex items-center gap-2">
                <Buildings size={24} className="text-[#7d64ff]" />
                Business Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                    Business Name *
                  </Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="Your registered business name"
                    className="mt-1 rounded-sm"
                    data-testid="kyc-business-name"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                    Business Type *
                  </Label>
                  <Select 
                    value={formData.business_type} 
                    onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="kyc-business-type">
                      <SelectValue placeholder="Select business type" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                    Registered Address
                  </Label>
                  <Textarea
                    value={formData.business_address}
                    onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                    placeholder="Full registered address"
                    rows={2}
                    className="mt-1 rounded-sm"
                    data-testid="kyc-address"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                    City
                  </Label>
                  <Input
                    value={formData.business_city}
                    onChange={(e) => setFormData({ ...formData, business_city: e.target.value })}
                    className="mt-1 rounded-sm"
                    data-testid="kyc-city"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                    State
                  </Label>
                  <Select 
                    value={formData.business_state} 
                    onValueChange={(value) => setFormData({ ...formData, business_state: value })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="kyc-state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                    Pincode
                  </Label>
                  <Input
                    value={formData.business_pincode}
                    onChange={(e) => setFormData({ ...formData, business_pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    placeholder="6-digit pincode"
                    maxLength={6}
                    className="mt-1 rounded-sm"
                    data-testid="kyc-pincode"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={() => setCurrentStep(2)}
                  className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
                >
                  Next: Tax & Bank Info <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Tax & Bank Information */}
          {currentStep === 2 && (
            <div className="bg-white border border-[#e5e5ea] p-6">
              <h3 className="font-bold text-lg text-[#0A0A0A] mb-6 flex items-center gap-2">
                <Bank size={24} className="text-[#7d64ff]" />
                Tax & Bank Information
              </h3>
              
              <div className="space-y-6">
                <div className="p-4 bg-[#fcfcfc] border border-[#e5e5ea]">
                  <h4 className="font-semibold mb-4">Tax Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                        GST Number *
                      </Label>
                      <Input
                        value={formData.gst_number}
                        onChange={(e) => setFormData({ ...formData, gst_number: e.target.value.toUpperCase() })}
                        placeholder="22AAAAA0000A1Z5"
                        maxLength={15}
                        className="mt-1 rounded-sm font-mono"
                        data-testid="kyc-gst"
                      />
                      <p className="text-xs text-[#5a5c87] mt-1">15-character GST identification number</p>
                    </div>

                    <div>
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                        PAN Number *
                      </Label>
                      <Input
                        value={formData.pan_number}
                        onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                        placeholder="AAAAA0000A"
                        maxLength={10}
                        className="mt-1 rounded-sm font-mono"
                        data-testid="kyc-pan"
                      />
                      <p className="text-xs text-[#5a5c87] mt-1">10-character PAN number</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-[#fcfcfc] border border-[#e5e5ea]">
                  <h4 className="font-semibold mb-4">Bank Account Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                        Bank Name *
                      </Label>
                      <Input
                        value={formData.bank_name}
                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                        placeholder="e.g., HDFC Bank"
                        className="mt-1 rounded-sm"
                        data-testid="kyc-bank-name"
                      />
                    </div>

                    <div>
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                        Account Holder Name
                      </Label>
                      <Input
                        value={formData.bank_account_holder}
                        onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })}
                        placeholder="Name as per bank records"
                        className="mt-1 rounded-sm"
                        data-testid="kyc-account-holder"
                      />
                    </div>

                    <div>
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                        Account Number *
                      </Label>
                      <Input
                        value={formData.bank_account_number}
                        onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value.replace(/\D/g, '') })}
                        placeholder="Enter account number"
                        className="mt-1 rounded-sm font-mono"
                        data-testid="kyc-account-number"
                      />
                    </div>

                    <div>
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                        IFSC Code *
                      </Label>
                      <Input
                        value={formData.bank_ifsc}
                        onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value.toUpperCase() })}
                        placeholder="HDFC0001234"
                        maxLength={11}
                        className="mt-1 rounded-sm font-mono"
                        data-testid="kyc-ifsc"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  Back
                </Button>
                <Button 
                  onClick={() => setCurrentStep(3)}
                  className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
                >
                  Next: Documents <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Document Upload */}
          {currentStep === 3 && (
            <div className="bg-white border border-[#e5e5ea] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg text-[#0A0A0A] flex items-center gap-2">
                  <FileText size={24} className="text-[#7d64ff]" />
                  Upload Documents
                </h3>
                <Button 
                  onClick={() => setUploadDialogOpen(true)}
                  className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
                  data-testid="upload-doc-btn"
                >
                  <Upload size={18} className="mr-2" />
                  Upload Document
                </Button>
              </div>
              
              {/* Required Documents Checklist */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {DOCUMENT_TYPES.map(docType => {
                  const uploaded = documents.find(d => d.document_type === docType.value);
                  return (
                    <div 
                      key={docType.value}
                      className={`p-4 border rounded ${
                        uploaded 
                          ? 'border-[#28cd8c] bg-[#28cd8c]/5' 
                          : docType.required 
                          ? 'border-[#ea4b5a]/30 bg-[#ea4b5a]/5'
                          : 'border-[#e5e5ea]'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{docType.label}</span>
                            {docType.required && <span className="text-xs text-[#ea4b5a]">Required</span>}
                          </div>
                          <p className="text-xs text-[#5a5c87] mt-1">{docType.description}</p>
                        </div>
                        {uploaded ? (
                          <CheckCircle size={24} className="text-[#28cd8c]" weight="fill" />
                        ) : (
                          <div className="w-6 h-6 rounded-full border-2 border-[#e5e5ea]" />
                        )}
                      </div>
                      {uploaded && (
                        <div className="mt-3 flex items-center justify-between text-xs">
                          <span className="text-[#5a5c87] truncate max-w-[150px]">{uploaded.original_filename}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#ea4b5a] h-6 px-2"
                            onClick={() => handleDeleteDocument(uploaded.document_id)}
                          >
                            <Trash size={14} />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Uploaded Documents List */}
              {documents.length > 0 && (
                <div className="border-t border-[#e5e5ea] pt-4">
                  <h4 className="font-semibold mb-3">Uploaded Documents ({documents.length})</h4>
                  <div className="space-y-2">
                    {documents.map(doc => (
                      <div 
                        key={doc.document_id}
                        className="flex items-center justify-between p-3 bg-[#fcfcfc] border border-[#e5e5ea] rounded"
                      >
                        <div className="flex items-center gap-3">
                          <File size={24} className="text-[#7d64ff]" />
                          <div>
                            <p className="font-medium text-sm">{doc.original_filename}</p>
                            <p className="text-xs text-[#5a5c87]">{getDocumentLabel(doc.document_type)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`${API_URL}/api/kyc/document/${doc.document_id}`, '_blank')}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#ea4b5a]"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(2)}>
                  Back
                </Button>
                <Button 
                  onClick={() => setCurrentStep(4)}
                  className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
                >
                  Next: Review <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Review & Submit */}
          {currentStep === 4 && (
            <div className="bg-white border border-[#e5e5ea] p-6">
              <h3 className="font-bold text-lg text-[#0A0A0A] mb-6 flex items-center gap-2">
                <CheckCircle size={24} className="text-[#7d64ff]" />
                Review & Submit
              </h3>
              
              <div className="space-y-6">
                {/* Business Summary */}
                <div className="p-4 bg-[#fcfcfc] border border-[#e5e5ea]">
                  <h4 className="font-semibold mb-3">Business Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-[#5a5c87]">Name:</span> <span className="font-medium">{formData.business_name}</span></div>
                    <div><span className="text-[#5a5c87]">Type:</span> <span className="font-medium">{formData.business_type}</span></div>
                    <div className="col-span-2"><span className="text-[#5a5c87]">Address:</span> <span className="font-medium">{formData.business_address}, {formData.business_city}, {formData.business_state} - {formData.business_pincode}</span></div>
                  </div>
                </div>

                {/* Tax Summary */}
                <div className="p-4 bg-[#fcfcfc] border border-[#e5e5ea]">
                  <h4 className="font-semibold mb-3">Tax Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-[#5a5c87]">GST:</span> <span className="font-mono font-medium">{formData.gst_number}</span></div>
                    <div><span className="text-[#5a5c87]">PAN:</span> <span className="font-mono font-medium">{formData.pan_number}</span></div>
                  </div>
                </div>

                {/* Bank Summary */}
                <div className="p-4 bg-[#fcfcfc] border border-[#e5e5ea]">
                  <h4 className="font-semibold mb-3">Bank Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-[#5a5c87]">Bank:</span> <span className="font-medium">{formData.bank_name}</span></div>
                    <div><span className="text-[#5a5c87]">IFSC:</span> <span className="font-mono font-medium">{formData.bank_ifsc}</span></div>
                    <div><span className="text-[#5a5c87]">Account:</span> <span className="font-mono font-medium">****{formData.bank_account_number.slice(-4)}</span></div>
                    <div><span className="text-[#5a5c87]">Holder:</span> <span className="font-medium">{formData.bank_account_holder}</span></div>
                  </div>
                </div>

                {/* Documents Summary */}
                <div className="p-4 bg-[#fcfcfc] border border-[#e5e5ea]">
                  <h4 className="font-semibold mb-3">Documents ({documents.length})</h4>
                  <div className="flex flex-wrap gap-2">
                    {documents.map(doc => (
                      <span key={doc.document_id} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-[#e5e5ea] rounded text-sm">
                        <File size={14} />
                        {getDocumentLabel(doc.document_type)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-[#7d64ff]/5 border border-[#7d64ff]/20 rounded">
                <p className="text-sm text-[#7d64ff]">
                  By submitting, you confirm that all information provided is accurate and you agree to our verification terms.
                </p>
              </div>

              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(3)}>
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-[#28cd8c] hover:bg-[#1fb076] text-white px-8"
                  data-testid="submit-kyc-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit KYC for Verification'}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Submitted/Approved View */
        kycData && (
          <div className="bg-white border border-[#e5e5ea] p-6">
            <h3 className="font-bold text-lg text-[#0A0A0A] mb-6">Submitted Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-[#fcfcfc] border border-[#e5e5ea]">
                <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-2">Business</h4>
                <p className="font-semibold">{kycData.business_name}</p>
                <p className="text-sm text-[#5a5c87]">{kycData.business_type}</p>
              </div>
              
              <div className="p-4 bg-[#fcfcfc] border border-[#e5e5ea]">
                <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-2">GST</h4>
                <p className="font-mono font-semibold">{kycData.gst_number}</p>
              </div>
              
              <div className="p-4 bg-[#fcfcfc] border border-[#e5e5ea]">
                <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-2">PAN</h4>
                <p className="font-mono font-semibold">{kycData.pan_number}</p>
              </div>
            </div>
            
            {kycData.documents && kycData.documents.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold mb-3">Submitted Documents</h4>
                <div className="flex flex-wrap gap-2">
                  {kycData.documents.map((doc, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#fcfcfc] border border-[#e5e5ea] rounded">
                      <File size={16} />
                      {getDocumentLabel(doc.document_type)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold">Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                Document Type *
              </Label>
              <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <span>{type.label}</span>
                        {type.required && <span className="text-xs text-[#ea4b5a] ml-2">(Required)</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                File
              </Label>
              <div className="mt-1">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#e5e5ea] rounded cursor-pointer hover:bg-[#fcfcfc] transition-colors">
                  <Upload size={32} className="text-[#5a5c87] mb-2" />
                  <span className="text-sm text-[#5a5c87]">Click to upload or drag & drop</span>
                  <span className="text-xs text-[#5a5c87] mt-1">PDF, JPG, PNG (Max 5MB)</span>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    disabled={uploading || !selectedDocType}
                  />
                </label>
              </div>
            </div>

            {uploading && (
              <div className="flex items-center justify-center p-4">
                <div className="spinner"></div>
                <span className="ml-2 text-sm">Uploading...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
